import { allMapsImageData, allPieces, ICellImage, images } from "@/utils/data"
import classNames from "classnames"
// import goToJailImage from "@/images/boards/default/go-to-jail.png"
import inJailImage from "@/images/boards/default/in-jail.png"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setGlobalElement } from "@/store/globalSlice"
import { mapDesigns } from "@mono/mapDesigns"
import Pieces from "@/Components/Pieces"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import HouseItemOriginal from "./HouseItemOriginal"
import { MortgageOverlay } from "./MortgageOverlay"
import Color from "color"
import { useSelector } from "react-redux"
import { isMobile } from "@/utils/utils"
import PropertyBigCardModal from "@/Modals/PropertyBigCardModal"
import Money from "@/Components/Money"
interface BoardCellProps {
  cell: ICell
  higherZIndex: boolean
  ownerImage?: IPiece
  lowBrightness: boolean
  isMortgaged: boolean
  shouldShowMortgageOverlay: boolean
  myHousesCount: number
  myHouseRemoveAnimations: IHouseAnimation[]
  shouldShowDoubleRentLabel: boolean
  cellImageData?: ICellImage
  brightnessClass: string
  myGroup?: IPropertyGroup
  rowClass: string
  cornerNumber: number | null
  hasHouseAnimation: boolean
  houseBuildModeActive: boolean
  houseBuildMode: "buy" | "sell" | null
  isBuildEligible: boolean
  buildPrice: number | null
  newRent: number | null
  onBuildClick: () => void
  isForceDowngradeTarget?: boolean
  // Trade-from-board: when the create-trade modal is open, eligible properties
  // become clickable to add/remove them from the offer.
  tradeSelectMode?: boolean
  tradeSelectionType?: TradeCardSelectionType
  isTradeHovered?: boolean
  onTradeToggle?: () => void
  // Board-picker mode for the board-targeted action cards: while active,
  // clicking an eligible cell plays the card on it instead of opening the
  // property card. Not restricted to property cells — the swap-position card
  // targets whichever tile an opponent is standing on, which is just as often
  // Go, jail or a chance tile.
  boardPickerSelectMode?: boolean
  isBoardPickerEligible?: boolean
  onBoardPickerSelect?: () => void
}

const BoardCell: React.FC<BoardCellProps> = ({
  cell,
  myHousesCount,
  myHouseRemoveAnimations,
  ownerImage,
  shouldShowDoubleRentLabel,
  shouldShowMortgageOverlay,
  isMortgaged,
  lowBrightness,
  brightnessClass,
  cellImageData,
  higherZIndex,
  myGroup,
  rowClass,
  cornerNumber,
  hasHouseAnimation,
  houseBuildModeActive,
  houseBuildMode,
  isBuildEligible,
  buildPrice,
  newRent,
  onBuildClick,
  isForceDowngradeTarget,
  tradeSelectMode,
  tradeSelectionType,
  isTradeHovered,
  onTradeToggle,
  boardPickerSelectMode,
  isBoardPickerEligible,
  onBoardPickerSelect,
}) => {
  const [showPropertyModal, setShowPropertyModal] = useState(false)
  const [isClicked, setIsClicked] = useState(false)
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleCellClick = useCallback(
    (e: React.MouseEvent) => {
      // Board-picker mode for a board-targeted action card: a click on an
      // eligible cell plays the card (a no-op otherwise) instead of opening
      // the property card. Checked before the property-only guard below,
      // since the swap card's targets are tiles of every type.
      if (boardPickerSelectMode) {
        e.stopPropagation()
        onBoardPickerSelect?.()
        return
      }

      // Only handle clicks for property cells
      if (cell.type !== "property") return

      // Prevent event bubbling
      e.stopPropagation()

      // While the create-trade modal is open, a click on an eligible property
      // adds/removes it from the offer instead of opening the property card.
      if (tradeSelectMode) {
        onTradeToggle?.()
        return
      }

      // In house build/sell mode, a click buys/sells a house on this property
      // (a no-op if it isn't eligible) instead of opening the property card.
      if (houseBuildModeActive) {
        onBuildClick()
        return
      }

      // Clear any existing timeout
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current)
      }

      // Visual feedback
      setIsClicked(true)
      clickTimeoutRef.current = setTimeout(() => setIsClicked(false), 300)

      // Open modal with slight delay for better UX
      setTimeout(() => {
        setShowPropertyModal(true)
      }, 150)
    },
    [
      cell.type,
      houseBuildModeActive,
      onBuildClick,
      tradeSelectMode,
      onTradeToggle,
      boardPickerSelectMode,
      onBoardPickerSelect,
    ]
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current)
      }
    }
  }, [])

  // Board-picker affordances, shared by every cell-type branch below: the
  // swap-position card can target a tile of any type, so the ring and cursors
  // can't live in the property branch alone. Ineligible cells are already
  // dimmed through `lowBrightness`; this only adds the invitation to click.
  // Spread *after* a branch's own cursor keys so it wins while picking.
  // The lit ring itself lives in .board-picker-eligible (App.css) rather than
  // in ring/shadow utilities: it has to paint over the cell's own artwork and
  // houses, which only a pseudo-element does.
  const boardPickerClasses = boardPickerSelectMode
    ? {
        "cursor-pointer": !!isBoardPickerEligible,
        "cursor-not-allowed": !isBoardPickerEligible,
        "board-picker-eligible": !!isBoardPickerEligible,
      }
    : {}

  const renderHouses = useCallback(() => {
    return (
      <>
        {myHousesCount === 5 ? (
          <HouseItemOriginal
            index={4}
            propertyOrder={cell.order}
          ></HouseItemOriginal>
        ) : (
          Array(myHousesCount)
            .fill(0)
            .map((_, i) => (
              <HouseItemOriginal
                index={i}
                propertyOrder={cell.order}
              ></HouseItemOriginal>
            ))
        )}

        {myHouseRemoveAnimations.map((item) => (
          <HouseItemOriginal
            index={item.index}
            propertyOrder={cell.order}
          ></HouseItemOriginal>
        ))}
      </>
    )
  }, [cell.order, myHousesCount, myHouseRemoveAnimations])
  if (cell.type === "property") {
    return (
      <>
        <div
          id={`cell-${cell.order}`}
          data-testid={`game.cell-${cell.order}`}
          onClick={handleCellClick}
          className={classNames(
            "flex flex-col w-full h-full bg-primary relative cell border border-black bg-center bg-no-repeat transition-all duration-200 group",
            {
              [`cell-group-${myGroup?.id}`]: true,
              mortgaged: isMortgaged,
              [brightnessClass]: lowBrightness,
              horizontal: cell.direction === "horizontal",
              vertical: cell.direction === "vertical",
              // While a house is animating, lift the whole cell above its
              // neighbours and the board centre so the house is fully visible.
              "!z-[90]": hasHouseAnimation,
              "z-10": higherZIndex && !hasHouseAnimation,
              [rowClass]: true,
              [`corner-${cornerNumber}`]: cornerNumber !== null,
              "touch-manipulation": true,
              "cursor-pointer": !tradeSelectMode,
              // Copy cursor marks a property that can be added/removed from the trade.
              "cursor-copy": !boardPickerSelectMode && tradeSelectMode,
              // Board-picker mode overrides the normal/trade cursor: eligible
              // cells invite a click, ineligible ones look inert (already
              // dimmed via lowBrightness above).
              ...boardPickerClasses,
              "scale-[0.96] brightness-90": isClicked,
              "ring-2 ring-white/60 ring-offset-1 ring-offset-black/50":
                isClicked,
              // The emerald ring serves two unrelated modes — a build-eligible
              // property in "buy" mode and a property being received in a board
              // trade. They must share a single key: repeating the same class
              // string twice in this literal silently drops the first branch.
              "ring-2 ring-inset z-20 ring-emerald-400":
                (houseBuildMode === "buy" && isBuildEligible) ||
                tradeSelectionType === "take",
              "ring-2 ring-inset z-20 ring-amber-400":
                houseBuildMode === "sell" && isBuildEligible,
              // Trade-from-board selection: red ring for a property being given,
              // emerald (above) for one being received, and a white hover hint
              // on eligible-but-unselected properties.
              "ring-2 ring-inset z-20 ring-red-500":
                tradeSelectionType === "give",
              // Eligible-but-unselected properties sit at reduced brightness so
              // hovering (full brightness + white ring) clearly previews them.
              "brightness-75 hover:brightness-110 hover:ring-2 hover:ring-inset hover:ring-white/70 hover:z-20":
                tradeSelectMode &&
                tradeSelectionType === "normal" &&
                !isTradeHovered,
              // Its card is hovered in the trade drawer. Everything this needs
              // (lift, brightness, pulsing amber halo) is in App.css — the lift
              // has to compose with the 180deg rotation rows 2 and 3 carry,
              // which no utility class can outrank.
              "trade-hovered": isTradeHovered,
              // Force-downgrade card target: aggressive red danger ring.
              "!ring-2 !ring-inset !z-[60] !ring-red-500":
                isForceDowngradeTarget,
              // Subtle hover effect for desktop to indicate clickability.
              // NOTE: the hover state must not animate `opacity`, `transform`
              // or `filter`. Rows 2 and 3 are rotated 180deg (App.css), and a
              // running compositor animation forces the rotated cell onto its
              // own render surface, where Chromium drops subpixel (LCD) text
              // antialiasing. The text goes grayscale-AA — visibly softer — for
              // the length of the transition, then snaps back. Rows 0/1 aren't
              // rotated so they keep LCD AA and look fine. Paint-only hover
              // changes (background-color) don't promote anything.
              // See the overlay div below for the actual highlight.
              // `hover:shadow-md` is also gone: on rows 2/3 the 180deg rotation
              // flips the shadow so it casts upward onto the neighbouring cell.
            }
          )}
          style={{
            // backgroundColor: new Color(myGroup?.color).alpha(0.8).string(),
            backgroundImage: `url(${cellImageData?.background})`,
            gridArea: "c" + cell.order,
            backgroundSize: "contain",
          }}
        >
          {/* Click feedback overlay */}
          {isClicked && (
            <div className="absolute inset-0 bg-white/20 rounded-sm pointer-events-none animate-pulse z-50" />
          )}
          {/* Trade-selected overlay: a colored tint + a check badge so a property
              that's been added to the offer reads as "in the trade" at a glance.
              The badge is normalized to upright per board side, like the build
              overlay, so the check never renders sideways/upside-down. */}
          {(tradeSelectionType === "give" || tradeSelectionType === "take") && (
            <div
              style={{
                writingMode: "horizontal-tb",
                transform:
                  rowClass === "row2" || rowClass === "row3"
                    ? "rotate(180deg)"
                    : undefined,
              }}
              className={classNames(
                "absolute inset-0 z-30 rounded-sm pointer-events-none flex items-center justify-center",
                {
                  "bg-red-500/20": tradeSelectionType === "give",
                  "bg-emerald-500/20": tradeSelectionType === "take",
                }
              )}
            >
              <span
                className={classNames(
                  "flex items-center justify-center h-5 w-5 rounded-full text-white text-[0.6rem] shadow-md ring-1 ring-white/80",
                  {
                    "bg-red-500": tradeSelectionType === "give",
                    "bg-emerald-500": tradeSelectionType === "take",
                  }
                )}
              >
                <i className="fa-solid fa-check"></i>
              </span>
            </div>
          )}
          {/* Force-downgrade danger highlight */}
          {isForceDowngradeTarget && (
            <div className="force-downgrade-flash absolute inset-0 rounded-sm pointer-events-none z-[55] bg-red-600/40" />
          )}
          {/* Subtle hover overlay for desktop */}
          {!isMobile() && (
            <div className="absolute inset-0 bg-transparent group-hover:bg-white/10 rounded-sm pointer-events-none z-40" />
          )}
          {shouldShowMortgageOverlay && (
            <MortgageOverlay propertyOrder={cell.order}></MortgageOverlay>
          )}
          <Pieces cell={cell}></Pieces>
          {myGroup?.withStrip && (
            <>
              <div
                className="cell-color flex justify-center items-center gap-1 mobile:gap-0.5"
                data-testid={`game.cell-strip-${cell.order}`}
                style={{ background: myGroup.color }}
              >
                {renderHouses()}
              </div>
            </>
          )}
          {shouldShowDoubleRentLabel && (
            <div className="absolute -top-1 flex flex-col justify-center items-center gap-0 left-1/2 text-sm -translate-x-1/2 whitespace-nowrap bg-red-600 text-white h-10 w-10  rounded-full font-bold">
              <span className="text-xxs ">2X</span>
              <span className="text-xxs">Rent</span>
            </div>
          )}
          {cell.propertyDetails && (
            <div
              // style={{
              //   backgroundColor: ownerPiece
              //     ? transparentPieceColor
              //     : "transparent",
              // }}
              className={classNames(
                "flex   gap-2 items-center justify-between overflow-hidden  h-full w-full  relative",
                {
                  "px-2": cell.direction === "vertical",
                  "py-2": cell.direction === "horizontal",
                  "flex-row":
                    cell.direction === "vertical" && myGroup?.id !== "8",
                  "flex-col":
                    cell.direction === "horizontal" || myGroup?.id === "8",
                }
              )}
            >
              {ownerImage?.placeholder && (
                <img
                  data-testid={`game.cell-owner-${cell.order}`}
                  src={ownerImage?.placeholder}
                  className={classNames(
                    "absolute top-1/2 left-1/2  -translate-x-1/2  opacity-[0.3] grayscale-[0]  z-[1]",
                    {
                      "w-[5rem] h-auto -translate-y-[40%]":
                        cell.direction === "horizontal",
                      "h-full md:h-[5rem] w-auto rotate-90 -translate-y-[50%]":
                        cell.direction === "vertical",
                    }
                  )}
                ></img>
              )}
              <div
                className={classNames(
                  "text-[12px] cell-title font-poppins text-center font-medium ",
                  {
                    "!hidden": myGroup?.id === "8",
                  }
                )}
              >
                {cell.propertyDetails && cell.propertyDetails.name}
              </div>
              <div></div>
              {cellImageData?.image && (
                <div className="relative flex-grow flex justify-center items-center h-full w-full">
                  <img
                    className={classNames(
                      "object-contain absolute h-full w-2/3"
                    )}
                    src={cellImageData?.image}
                    alt="Tile"
                  />
                </div>
              )}
              <div
                className={classNames(
                  " text-[0.4rem] md:text-[0.65rem] font-poppins text-center font-medium cell-price",
                  {
                    "mt-0":
                      cellImageData?.image && cell.direction === "horizontal",
                  }
                )}
              >
                M{cell.propertyDetails?.price}
              </div>
              {houseBuildMode && isBuildEligible && buildPrice !== null && (
                <div
                  className="absolute inset-0 z-20 flex flex-col overflow-hidden pointer-events-none bg-gradient-to-b from-white to-gray-100 ring-1 ring-black/15 shadow-md"
                  style={{
                    // The cell is rotated / writing-mode-flipped per board side
                    // (see App.css). Reset that here so the text always reads
                    // upright, like the property names do.
                    writingMode: "horizontal-tb",
                    transform:
                      rowClass === "row2" || rowClass === "row3"
                        ? "rotate(180deg)"
                        : undefined,
                  }}
                >
                  {/* Group-color header, mirroring the property card */}
                  <div
                    className="flex items-center justify-center shrink-0 w-full py-[1px]"
                    style={{ backgroundColor: myGroup?.color || "#000" }}
                  >
                    <span className="font-raleway font-bold uppercase tracking-widest text-white text-[0.55rem] leading-none drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]">
                      {houseBuildMode === "buy" ? "Build" : "Sell"}
                    </span>
                  </div>

                  {/* Body: price (primary) + rent (secondary) */}
                  <div className="flex-1 flex flex-col items-center justify-center gap-0.5 px-1 text-center">
                    <Money
                      money={buildPrice}
                      sign={houseBuildMode === "buy" ? "−" : "+"}
                      className={classNames(
                        "font-poppins font-extrabold text-xl leading-none gap-0.5",
                        {
                          "text-red-600": houseBuildMode === "buy",
                          "text-green-600": houseBuildMode === "sell",
                        }
                      )}
                    />
                    {newRent !== null && (
                      <>
                        <div className="h-px w-2/3 bg-gray-300/70" />
                        <div className="flex items-center gap-1 leading-none">
                          <span className="font-poppins font-bold text-gray-400 text-[0.55rem] uppercase tracking-wide">
                            Rent
                          </span>
                          <Money
                            money={newRent}
                            className="font-poppins font-bold text-gray-800 text-base leading-none gap-0.5"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {showPropertyModal && (
          <PropertyBigCardModal
            property={cell}
            onClose={() => setShowPropertyModal(false)}
          />
        )}
      </>
    )
  }
  if (cell.type === "action" || cell.type === "event") {
    return (
      <div
        id={`cell-${cell.order}`}
        style={{
          gridArea: "c" + cell.order,
        }}
        data-testid={`game.cell-${cell.order}`}
        onClick={handleCellClick}
        className={classNames(
          "grid grid-rows-[min-content_auto] gap-2   w-full h-full bg-primary cell border border-black relative bg-center bg-no-repeat",
          {
            ...boardPickerClasses,
            // "px-2": cell.direction === "vertical",
            // "py-2": cell.direction === "horizontal",

            horizontal: cell.direction === "horizontal",
            vertical: cell.direction === "vertical",
            "z-10": higherZIndex,
            [brightnessClass]: lowBrightness,
            [rowClass]: true,
            [`corner-${cornerNumber}`]: cornerNumber !== null,
          }
        )}
      >
        <Pieces cell={cell}></Pieces>
        {/* <div */}
        {/*   className={classNames( */}
        {/*     "text-[8px] cell-title font-poppins text-center font-medium  uppercase", */}
        {/*     { */}
        {/*       "mx-2": cell.direction === "vertical", */}
        {/*       "my-2": cell.direction === "horizontal", */}
        {/*     } */}
        {/*   )} */}
        {/* > */}
        {/*   {cell.type === "chance" ? "Chance" : "Community Chest"} */}
        {/* </div> */}
        <div></div>
        <div className="relative h-full flex justify-center items-center">
          <img
            className={classNames("object-contain absolute h-full w-auto ", {
              // "w-auto h-full min-h-0": true,
              // "w-full h-auto min-w-0": true,
              //
              // "h-1/2":
              //   cell.type === "action" && cell.direction === "horizontal",
              // "w-1/2": cell.type === "action" && cell.direction === "vertical",
              // "w-2/3": cell.type === "event" && cell.direction === "horizontal",
              // "h-2/3": cell.type === "event" && cell.direction === "vertical",
            })}
            src={cellImageData?.image}
            alt="Tile"
          />
        </div>
      </div>
    )
  }
  if (cell.type === "tax") {
    return (
      <div
        id={`cell-${cell.order}`}
        style={{
          gridArea: "c" + cell.order,
        }}
        data-testid={`game.cell-${cell.order}`}
        onClick={handleCellClick}
        className={classNames(
          "flex flex-col justify-between items-center   w-full h-full bg-primary cell border border-black relative bg-center bg-no-repeat ",
          {
            ...boardPickerClasses,
            // "py-2": cell.direction == "horizontal",
            // "px-2": cell.direction === "vertical",
            horizontal: cell.direction === "horizontal",
            vertical: cell.direction === "vertical",
            "z-100": true,
            [brightnessClass]: lowBrightness,
            [rowClass]: true,
            [`corner-${cornerNumber}`]: cornerNumber !== null,
          }
        )}
      >
        <Pieces cell={cell}></Pieces>
        {/* <div className="text-xs cell-title font-poppins text-center font-medium  uppercase cell-title-upper">
          {cell.taxDetails.name}
        </div> */}
        <img
          className={classNames("object-cover absolute  w-full h-full")}
          src={cellImageData?.image}
          alt="Tile"
        />
        {/* <div className="relative flex-grow flex justify-center items-center h-full w-full"></div> */}
        {/* <div className="text-[0.5rem] font-poppins text-center font-medium uppercase tax-price">
          PAY M{cell.taxDetails.price}
        </div> */}
      </div>
    )
  }
  if (cell.type === "go-to-jail") {
    return (
      <div
        id={`cell-${cell.order}`}
        style={{
          gridArea: "c" + cell.order,
        }}
        data-testid={`game.cell-${cell.order}`}
        onClick={handleCellClick}
        className={classNames(
          "flex flex-col  w-full h-full bg-primary cell border border-black relative bg-center bg-no-repeat",

          {
            ...boardPickerClasses,
            "px-2": cell.direction === "vertical",
            "py-2": cell.direction === "horizontal",
            horizontal: cell.direction === "horizontal",
            vertical: cell.direction === "vertical",
            "z-10": higherZIndex,
            [brightnessClass]: lowBrightness,
            [rowClass]: true,
            [`corner-${cornerNumber}`]: cornerNumber !== null,
          }
        )}
      >
        <Pieces cell={cell}></Pieces>

        {/* <div>
          <img
            className={classNames(
              "object-contain absolute top-1/2 left-1/2 origin-center rotate-90 -translate-x-1/2 - -translate-y-1/2",
              {
                "w-auto h-full min-h-0": cell.direction === "horizontal",
                "w-auto h-full min-w-0": cell.direction === "vertical",
              }
            )}
            src={goToJailImage}
            alt="Tile"
          />
        </div> */}
      </div>
    )
  }
  // if (cell.type === "jail") {
  //   return (
  //     <div
  //       id={`cell-${cell.order}`}
  //       style={{
  //         gridArea: "c" + cell.order,
  //       }}
  //       data-testid={`game.cell-${cell.order}`}
  //       className={classNames(
  //         "flex flex-col  w-full h-full bg-primary cell border border-black relative bg-center bg-no-repeat",

  //         {
  //           "px-2": cell.direction === "vertical",
  //           "py-2": cell.direction === "horizontal",
  //           horizontal: cell.direction === "horizontal",
  //           vertical: cell.direction === "vertical",
  //           "z-10": higherZIndex,
  //           [brightnessClass]: lowBrightness,
  //           [rowClass]: true,
  //         }
  //       )}
  //     >
  //       <Pieces cell={cell}></Pieces>
  //       <div className="absolute right-0 h-3/4 w-3/4">
  //         <img
  //           id="jail-area"
  //           className={classNames(
  //             "object-contain absolute top-0 right-0 origin-center rotate-90  h-full w-full border-b-2 border-r-2  border-black"
  //           )}
  //           src={inJailImage}
  //           alt="Tile"
  //         />
  //         <img
  //           ref={jailOverlayRef}
  //           src={jailOverlayImage}
  //           className="h-full w-full z-[101] absolute opacity-0"
  //           alt=""
  //         />
  //       </div>

  //       <div className="absolute text-xs font-poppins left-0 font-medium top-4 flex items-center justify-center w-1/4">
  //         JUST
  //       </div>
  //       <div
  //         className="absolute text-xs font-poppins bottom-0 font-medium left-0 flex items-center justify-center w-full h-1/4 "
  //         style={{ writingMode: "initial" }}
  //       >
  //         VISITING
  //       </div>
  //     </div>
  //   )
  // }

  return (
    <div
      id={`cell-${cell.order}`}
      onClick={handleCellClick}
      className={classNames(
        "flex flex-col w-full h-full bg-primary cell border border-black relative bg-center bg-no-repeat ",
        {
          ...boardPickerClasses,
          "z-10": higherZIndex,
          [brightnessClass]: lowBrightness,
          [`corner-${cornerNumber}`]: cornerNumber !== null,
        }
      )}
      data-testid={`game.cell-${cell.order}`}
      style={{
        backgroundImage: `url(${cellImageData?.background})`,
        backgroundSize: "contain",
        gridArea: "c" + cell.order,
        [rowClass]: true,
      }}
    >
      <Pieces cell={cell}></Pieces>
      <div
        className={classNames(
          "flex flex-col  gap-2 items-center justify-between  h-full w-full  ",
          {
            "px-2": cell.direction === "vertical",
            "py-2": cell.direction === "horizontal",
          }
        )}
      >
        {cellImageData?.image && (
          <div className="relative flex-grow flex justify-center items-center h-full w-full">
            <img
              className={classNames("object-contain absolute h-full w-auto")}
              src={cellImageData?.image}
              alt="Tile"
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default React.memo(BoardCell)
